/**
 * Ryan O'Dowd
 * 2022-05-06
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Keyboard,
  Platform,
  View,
} from 'react-native';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GameTemplate from '@containers/Practice/GameTemplate';
import Reference from '@containers/Practice/Reference';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const FirstLetters = (props: FirstLettersProps) => {
  const styles = hooks.useStyles(getStyles);

  const [_questionComplete, setQuestionComplete] = useState(false); // @TODO: abstract this?
  const [_isCorrect, setIsCorrect] = useState(false);
  const [_isIncorrect, setIsIncorrect] = useState(false);
  const [_showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [_blankTexts, setBlankTexts] = useState({});
  const [_guessInputWidths, setGuessInputWidths] = useState({});
  const [_hasSpellingMistake, setHasSpellingMistake] = useState(false);

  const firstRender = useRef(true);

  const verseNum = useMemo(() => {
    return props.passage.start_verse_number + Math.floor(Math.random() * (props.passage.end_verse_number - props.passage.start_verse_number + 1));
  }, [props.passage]);

  const singleVersePassage = {
    ...props.passage,
    start_verse_number: verseNum,
    end_verse_number: verseNum,
  };
  const verses = hooks.useGetVersesForPassages([singleVersePassage])[props.passage.id];
  const verse = utilities.removeVerseFormatting(verses[0].verseText);
  const verseWords = useMemo(() => {
    return verse.split(/[^A-Za-z0-9]/).filter((w) => w); // @TODO: we don't want to lose all punctuation!
  }, [verse]);

  const wordRefs = useMemo(() => {
    return verseWords.map(() => createRef());
  }, [verseWords]);

  let lettersToProvide = 3;
  if (props.difficulty === 'medium') {
    lettersToProvide = 2;
  } else if (props.difficulty === 'hard') {
    lettersToProvide = 1;
  }

  const _isWithinErrorMargin = (word1, word2) => {
    const exactMatch = word1 === word2;
    if (exactMatch) {
      return true;
    }

    if (word1.length < word2.length) {
      const temp = word1;
      word1 = word2;
      word2 = temp;
    }

    const word1Letters = word1.split('');
    const word2Letters = word2.split('');

    let numMismatches = 0;
    word1Letters.forEach((l, i) => {
      if (l !== word2Letters?.[i]) {
        numMismatches++;
      }
    });

    if (numMismatches === 2 && word1Letters.sort().join('') === word2Letters.sort().join('')) {
      // two letters are swapped
      return true;
    }

    if (word1.length - word2.length === 1) {
      // if they're off by one letter (spelling), then it should be a pass
      for (let i = word1.length - 1; i >= 0; i--) {
        const modifiedStr = word1.slice(0, i) + word1.slice(i + 1);
        if (modifiedStr === word2) {
          return true;
        }
      }
    }

    return numMismatches <= 1;
  };

  const _isWrongGuess = useCallback((wordIndex, blankText) => {
    // compare string values, not indexes (watch out for options that are the same word, even though the indexes are swapped)
    // @TODO: it shouldn't be possible for `guess` to be `null`; a value in `_currentGuesses` can be `null` temporarily due to unselecting an option from a blank, but the "check" button should be disabled when that's the case; seems to be a problem on `OnePlus 8T` and `B140DL` devices at the moment?
    // @TODO: ignore punctuation
    // @TODO: return guess?.value.toLocaleLowerCase() !== verse.sort((a, b) => a.index - b.index)[guessIndex].value.toLocaleLowerCase();
    const correctWord = verse.split(/[^A-Za-z0-9]/).filter((w) => w)[wordIndex].toLocaleLowerCase(); // @TODO: we don't want to lose all punctuation!
    const userWord = `${correctWord.slice(0, lettersToProvide)}${blankText}`.toLocaleLowerCase();

    return correctWord !== userWord;
  }, [verse, lettersToProvide]);

  useEffect(() => {
    if (verseWords.length === 1 && verseWords[0] === '') {
      // verse text is not in redux yet
      return;
    }

    const tempBlanks = {};
    while (Object.keys(tempBlanks).length === 0) {
      verseWords.forEach((word, index) => {
        // `1 / lettersToProvide` chance in needing to fill this word in
        const provideThisWord = Math.random() * lettersToProvide >= 1;

        if (word.length > 3 && !provideThisWord) {
          tempBlanks[index] = '';
        }
      });
    }

    if (firstRender.current) {
      setBlankTexts(tempBlanks);
      firstRender.current = false;
    }
  }, [lettersToProvide, verseWords]);

  const isCheckAnswerButtonDisabled = !!Object.values(_blankTexts).filter((t) => !t).length;

  const lessonWordsJsx = useMemo(() => {
    // @TODO: these should be settings or global variables
    const formattedWords = verseWords.map((word, index) => {
      // if (props.difficulty === 'easy') {
      //   // @TODO: show remaining letters instead of blanks (as placeholder text); or maybe not...
      // }

      // @TODO: if word is 3 characters or shorter, always display it as complete, no matter the difficulty
      const provided = _blankTexts[index] === undefined ? word : word.slice(0, lettersToProvide);
      const rest = _blankTexts[index] === undefined ? '' : word.slice(lettersToProvide);

      const isCompleteWord = !rest.length;

      if (!provided) {
        // @TODO: temp hack until punctuation is added back
        return null;
      }

      const guessIsIncorrect = rest && _questionComplete && _isWrongGuess(index, _blankTexts[index]);
      let textColor = isCompleteWord ? styles.colors.primaryExtraDark : styles.colors.accentLight;
      if (guessIsIncorrect) {
        textColor = styles.colors.alert;
      }

      const minInputWidth = rest.length * (styles.globalScale * 12); // @TODO: globalScale may be too big of a scalar in this use case // @TODO: don't include numLetters if difficulty is medium or hard?

      return (
        <React.Fragment key={`${word}-${index}`}>
          <Text
            style={[styles.guessText, styles.hiddenText]}
            pointerEvents='none'
            onLayout={(e) => {
              const textWidth = e.nativeEvent.layout.width;
              setGuessInputWidths((curr) => {
                return {...curr, [`${index}`]: textWidth}; // @TODO: plus buffer?
              });
            }}
          >
            {_blankTexts[`${index}`]}
          </Text>
          <View// @TODO: this should be a view?
            key={`${rest}-${index}`}
            style={styles.providedTextWrapper}
          >
            <Text
              style={[styles.providedText, isCompleteWord && styles.completeWord, guessIsIncorrect && styles.wrongGuessText]}
            >
              {provided}{/* @TODO: wrap in text component? */}
            </Text>
            {!!rest &&
              <TextInput// @TODO: some of these inputs are covered by keyboard if the verse is long enough
                key={`${rest}-${index}`}
                ref={wordRefs[index]}
                style={styles.guessTextWrapper}
                noFormControl={true}
                contentStyle={[styles.guessText, styles.guessTextWidths(Math.max(_guessInputWidths[`${index}`] || 0, minInputWidth)), guessIsIncorrect && styles.wrongGuessText]}// @TODO: most of these aren't doing anything // @TODO: 16 is good for the letter m, but it's too big for other letters // @TODO: minWidth used to be sufficient for letting input autoscale, but 0.73.1 (or similar) makes the input a fixed width that's too big
                editable={!_questionComplete}
                textColor={textColor}
                autoFocus={index === Object.keys(_blankTexts).map((stringIndex) => +stringIndex).sort((a, b) => a - b)[0]}
                maxLength={rest.length}// @TODO: this isn't working
                autoCapitalize='none'
                autoCorrect={false}
                blurOnSubmit={false}
                returnKeyType='next'// @TODO: unless this is the last one
                underlineColor='#00000000'// @TODO: not working for removing underline on android
                activeUnderlineColor='#00000000'// @TODO: not working for removing underline on android
                value={_blankTexts[`${index}`]}
                onChangeText={(t) => {
                  t = t.replace(/\s/g, '');
                  setBlankTexts({..._blankTexts, [`${index}`]: t});
                  const blankIndexes = Object.keys(_blankTexts).map((stringIndex) => +stringIndex).sort((a, b) => a - b);
                  const currBlankIndex = blankIndexes.indexOf(index);
                  const nextBlankIndex = (currBlankIndex + 1) < blankIndexes.length ? blankIndexes[currBlankIndex + 1] : null;
                  if ((t.length === rest.length) && nextBlankIndex) {
                    wordRefs[nextBlankIndex]?.current?.focus();
                  }
                }}
                onKeyPress={({nativeEvent}) => {
                  if (nativeEvent.key === 'Backspace' && _blankTexts[`${index}`] === '') {
                    if (Platform.OS === 'web') {
                      // @NOTE: this method only exists on web (but that's fine becuase the bug doesn't exist on mobile)
                      (nativeEvent as any).preventDefault();
                    }
                    const blankIndexes = Object.keys(_blankTexts).map((stringIndex) => +stringIndex).sort((a, b) => a - b);
                    const currBlankIndex = blankIndexes.indexOf(index);
                    const prevBlankIndex = (currBlankIndex - 1) >= 0 ? blankIndexes[currBlankIndex - 1] : null;
                    if (prevBlankIndex !== null) {
                      wordRefs[prevBlankIndex]?.current?.focus();
                      setBlankTexts({..._blankTexts, [`${prevBlankIndex}`]: _blankTexts[`${prevBlankIndex}`].slice(0, -1)});
                    }
                  }
                  /* @TODO: looks like this isn't supported at the moment: https://github.com/facebook/react-native/issues/36644
                  console.log(nativeEvent.key);
                  if (nativeEvent.key === 'Enter' && !isCheckAnswerButtonDisabled) {
                    console.log('check');
                    onCheckAnswer();
                  }
                  */
                }}
                keyboardType='ascii-capable'
              />
            }
          </View>
        </React.Fragment>
      );
    });

    return formattedWords;
  }, [verseWords, _blankTexts, _isWrongGuess, _questionComplete, lettersToProvide, styles, wordRefs, _guessInputWidths]);

  const _highlightWrongWords = () => {
    const words = verse.split(/[^A-Za-z0-9]/).filter((w) => w).map((w) => `${w} `); // @TODO: add punctualtion back here

    words.forEach((__, index) => {
      const guessText = _blankTexts[index];
      if (guessText && _isWrongGuess(index, guessText)) {
        words[index] = <Text key={index} style={[styles.correctAnswerText, styles.correctedWrongGuess]}>{words[index]}</Text>;
      }
    });

    return words;
  };

  const onCheckAnswer = () => {
    props.onCheckAnswer();
    Keyboard.dismiss();
    // @TODO: if failed, add to practice
    // @TODO: add score
    setQuestionComplete(true);
    // @TODO: set state for correct or not and display value to user
    if (Object.entries(_blankTexts).every(([blankIndex, blankText]) => {
      const correctWord = verse.split(/[^A-Za-z0-9]/).filter((w) => w)[blankIndex].toLocaleLowerCase(); // @TODO: we don't want to lose all punctuation! // @TODO: add punctualtion back here
      const userWord = `${correctWord.slice(0, lettersToProvide)}${blankText}`.toLocaleLowerCase();
      if (!_isWrongGuess(blankIndex, blankText)) {
        return true;
      } else if (_isWithinErrorMargin(correctWord, userWord)) {
        setHasSpellingMistake(true);
        return true;
      }

      return false;
    })) {
      // @TODO: play sound
      setIsCorrect(true);
    } else {
      // @TODO: play sound
      setIsIncorrect(true);
    }
    setShowCorrectAnswer(true);
  };

  return (
    <GameTemplate
      subHeaderHeight={props.subHeaderHeight}
      questionComplete={_questionComplete}
      isIncorrect={_isIncorrect}
      isCorrect={_isCorrect}
      isCheckAnswerButtonDisabled={isCheckAnswerButtonDisabled}
      onPass={() => props.onPass(_isCorrect, 1)}
      onCheckAnswer={onCheckAnswer}
    >
      <Reference passage={props.passage.cloneWithChanges({startVerseNumber: verseNum, endVerseNumber: verseNum})} />
      <View style={styles.lessonTextWrapper}>
        {lessonWordsJsx}
      </View>
      {((_showCorrectAnswer && !_isCorrect) || _hasSpellingMistake) &&
        <View style={styles.correctAnswer}>
          {/* @TODO: :tada: could be replaced by graphics/gifs (for all test types) */}
          {(_isIncorrect || _hasSpellingMistake) &&
            <Text style={styles.correctAnswerText}>
              {_highlightWrongWords()}
            </Text>
          }
        </View>
      }
    </GameTemplate>
  );
};

class FirstLettersProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onCheckAnswer: () => void;
  passage: any;
  onPass: (isCorrect: boolean, versesReviewed: number) => void;
  subHeaderHeight: number;
};

export default FirstLetters;
